// File: src/services/mainApi.js
// thin client for the v2 subscription admin
// endpoints. These now live on the ADMIN worker (mirrored from the main
// worker per), so this client targets the same
// origin as adminApi.js — no more port-swap, no CORS surprises.
//
// Keeping this as a separate module (rather than folding into adminApi.js)
// for now because the URL/auth wiring is intentionally identical to
// adminApi.js and a future refactor can consolidate both at once.

import axios from 'axios';
import { auth } from '../firebaseConfig';

const API_URL = process.env.REACT_APP_API_ENDPOINT
    || (process.env.NODE_ENV === 'development' ? 'http://localhost:8788' : 'https://api-admin.ads.defoai.com');

function getCurrentUser() {
    if (auth.currentUser) return auth.currentUser;
    if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_TEST_AUTH_SECRET) {
        try {
            const storedMock = localStorage.getItem('firebase-auth-mock');
            if (storedMock) {
                const parsed = JSON.parse(storedMock);
                if (parsed.isAuthenticated && parsed.user) {
                    const { uid, email } = parsed.user;
                    const secret = process.env.REACT_APP_TEST_AUTH_SECRET;
                    return {
                        ...parsed.user,
                        getIdToken: async () => `${secret}:${uid}:${email}`,
                    };
                }
            }
        } catch (_e) { /* ignore */ }
    }
    return null;
}

async function client() {
    const user = getCurrentUser();
    if (!user) throw new Error('Not authenticated');
    const idToken = await user.getIdToken();
    return axios.create({
        baseURL: API_URL,
        headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json',
        },
        // The admin worker sits behind Cloudflare Access. The browser must
        // send the `CF_Authorization` cookie on every cross-origin XHR or
        // Access 302-redirects the request to the login flow, which the
        // browser surfaces as a CORS failure. Matches adminApi.js.
        withCredentials: true,
        timeout: 30000,
    });
}

export const mainApi = {
    // GET /admin/users/:uid/subscription
    async getUserSubscription(uid) {
        const c = await client();
        const res = await c.get(`/admin/users/${encodeURIComponent(uid)}/subscription`);
        return res.data;
    },

    // PUT /admin/users/:uid/subscription/state
    async updateUserSubscriptionState(uid, body) {
        const c = await client();
        const res = await c.put(`/admin/users/${encodeURIComponent(uid)}/subscription/state`, body);
        return res.data;
    },

    // POST /admin/users/:uid/credits
    async grantCredits(uid, aiActions, expiresAt = null) {
        const c = await client();
        const body = { aiActions };
        if (expiresAt) body.expiresAt = expiresAt;
        const res = await c.post(`/admin/users/${encodeURIComponent(uid)}/credits`, body);
        return res.data;
    },

    // DELETE /admin/users/:uid/credits
    async deleteCredits(uid) {
        const c = await client();
        const res = await c.delete(`/admin/users/${encodeURIComponent(uid)}/credits`);
        return res.data;
    },

    // GET /admin/plans
    async listPlans() {
        const c = await client();
        const res = await c.get('/admin/plans');
        return res.data;
    },

    // PUT /admin/plans/:id
    async updatePlan(planId, updates) {
        const c = await client();
        const res = await c.put(`/admin/plans/${encodeURIComponent(planId)}`, updates);
        return res.data;
    },

    // GET /admin/subscription/overview[?fresh=1]
    async getSubscriptionOverview({ fresh = false } = {}) {
        const c = await client();
        const url = fresh ? '/admin/subscription/overview?fresh=1' : '/admin/subscription/overview';
        const res = await c.get(url);
        return res.data;
    },

    // ============================================================
    // world-class Plans editor.
    // ============================================================

    // POST /admin/plans  — create a new plan.
    async createPlan(body) {
        const c = await client();
        const res = await c.post('/admin/plans', body);
        return res.data;
    },

    // POST /admin/plans/:id/archive
    async archivePlan(planId) {
        const c = await client();
        const res = await c.post(`/admin/plans/${encodeURIComponent(planId)}/archive`);
        return res.data;
    },

    // POST /admin/plans/:id/unarchive
    async unarchivePlan(planId) {
        const c = await client();
        const res = await c.post(`/admin/plans/${encodeURIComponent(planId)}/unarchive`);
        return res.data;
    },

    // POST /admin/plans/:id/duplicate
    async duplicatePlan(planId) {
        const c = await client();
        const res = await c.post(`/admin/plans/${encodeURIComponent(planId)}/duplicate`);
        return res.data;
    },

    // GET /admin/plans/:id/history
    async getPlanHistory(planId) {
        const c = await client();
        const res = await c.get(`/admin/plans/${encodeURIComponent(planId)}/history`);
        return res.data;
    },

    // GET /admin/plans/:id/users-count
    async countUsersOnPlan(planId) {
        const c = await client();
        const res = await c.get(`/admin/plans/${encodeURIComponent(planId)}/users-count`);
        return res.data;
    },

    // POST /admin/plans/:id/estimate
    async estimatePlan(planId, body) {
        const c = await client();
        const res = await c.post(`/admin/plans/${encodeURIComponent(planId)}/estimate`, body);
        return res.data;
    },

    // GET /admin/pricing/cost-config
    async getCostConfig() {
        const c = await client();
        const res = await c.get('/admin/pricing/cost-config');
        return res.data;
    },

    // PUT /admin/pricing/cost-config
    async updateCostConfig(updates) {
        const c = await client();
        const res = await c.put('/admin/pricing/cost-config', updates);
        return res.data;
    },

    // GET /admin/stripe/prices?productId=&active=
    async listStripePrices({ productId = null, active = true } = {}) {
        const c = await client();
        const params = new URLSearchParams();
        if (productId) params.set('productId', productId);
        if (!active) params.set('active', 'false');
        const qs = params.toString();
        const res = await c.get(`/admin/stripe/prices${qs ? `?${qs}` : ''}`);
        return res.data;
    },

    // GET /admin/stripe/products?active=
    async listStripeProducts({ active = true } = {}) {
        const c = await client();
        const res = await c.get(`/admin/stripe/products${active ? '' : '?active=false'}`);
        return res.data;
    },

    // POST /admin/stripe/verify-price
    async verifyStripePrice(body) {
        const c = await client();
        const res = await c.post('/admin/stripe/verify-price', body);
        return res.data;
    },

    // POST /admin/stripe/create-price
    async createStripePrice(body) {
        const c = await client();
        const res = await c.post('/admin/stripe/create-price', body);
        return res.data;
    },

    // POST /admin/plans/:id/translate — AI-translate the plan's English
    // marketing copy into the requested locales (defaults to all non-EN
    // supported). Returns { plan, translated: [locales], errors: {locale: msg} }.
    async translatePlan(planId, body = {}) {
        const c = await client();
        const res = await c.post(`/admin/plans/${encodeURIComponent(planId)}/translate`, body);
        return res.data;
    },

    // ============================================================
    // Promotion codes admin CRUD.
    // ============================================================

    // GET /admin/promos  — list all promo codes (active + archived).
    async listPromoCodes() {
        const c = await client();
        const res = await c.get('/admin/promos');
        return res.data;
    },

    // POST /admin/promos  — create a new promo code.
    async createPromoCode(body) {
        const c = await client();
        const res = await c.post('/admin/promos', body);
        return res.data;
    },

    // GET /admin/promos/:code  — fetch one promo code (+ redeemerCount).
    async getPromoCode(code) {
        const c = await client();
        const res = await c.get(`/admin/promos/${encodeURIComponent(code)}`);
        return res.data;
    },

    // PUT /admin/promos/:code  — update description/maxUses/expiresAt/status.
    async updatePromoCode(code, updates) {
        const c = await client();
        const res = await c.put(`/admin/promos/${encodeURIComponent(code)}`, updates);
        return res.data;
    },

    // POST /admin/promos/:code/archive  — archive a promo code.
    async archivePromoCode(code) {
        const c = await client();
        const res = await c.post(`/admin/promos/${encodeURIComponent(code)}/archive`);
        return res.data;
    },
};
